import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/**
 * Handles full-page Google OAuth redirects from the backend.
 * Tokens are delivered in the URL hash fragment to avoid server-side logging.
 */
const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;

    if (!hash) {
      setError('Missing OAuth credentials. Please try signing in again.');
      return;
    }

    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const redirectPath = params.get('redirect_path');
    const userRaw = params.get('user');

    if (!accessToken || !userRaw) {
      setError('Incomplete OAuth response. Please try signing in again.');
      return;
    }

    try {
      const user = JSON.parse(userRaw);

      localStorage.setItem('authToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      const userData = {
        id: user.id?.toString?.() ?? String(user.id),
        email: user.email,
        name: user.full_name || user.name || '',
        role: user.role,
        tier: user.tier || 'free',
        timezone: user.timezone || 'Asia/Kolkata',
        phone: user.phone_number || undefined,
        emailVerifiedAt: user.email_verified_at || null,
        phoneVerifiedAt: user.phone_verified_at || null,
        preferredVerificationMethod: user.preferred_verification_method || null,
      };
      localStorage.setItem('user', JSON.stringify(userData));

      window.history.replaceState(null, '', window.location.pathname);

      if (user.role === 'pending') {
        localStorage.setItem(
          'oauthUserData',
          JSON.stringify({
            email: user.email,
            name: user.full_name || user.name || '',
            fromOAuth: true,
          })
        );
        navigate('/register?mode=signup&oauth=true', { replace: true });
        window.location.reload();
        return;
      }

      const destination =
        redirectPath ||
        (user.role === 'customer'
          ? '/customer/dashboard'
          : user.role === 'tailor'
            ? '/business/dashboard'
            : user.role === 'admin'
              ? '/admin/dashboard'
              : '/customer/dashboard');

      navigate(destination, { replace: true });
      window.location.reload();
    } catch {
      setError('Failed to process OAuth login. Please try again.');
    }
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full rounded-xl bg-white p-8 shadow text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
